import { Link } from '@tanstack/react-router'
import { useTranslations } from 'gt-react'
import { cn } from '@/lib/utils'

/**
 * Site footer with four link columns (Product, Company, Resources,
 * Legal), brand blurb, socials, and copyright. Entirely static —
 * no animation to keep scroll-to-bottom snappy.
 */
export function Footer() {
  const t = useTranslations('footer')
  const year = new Date().getFullYear()

  return (
    <footer className="border-t border-border bg-muted/30">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-5">
          {/* Brand column */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-2">
              <div
                className="h-8 w-8 rounded-lg"
                style={{
                  background: 'linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%)',
                }}
              />
              <span className="font-semibold text-base">Cloud ERP</span>
            </div>
            <p className="mt-4 max-w-xs text-sm text-muted-foreground">{t('tagline')}</p>
            <div className="mt-6 flex items-center gap-3">
              <SocialLink href="https://github.com" label="GitHub">
                <GitHubIcon />
              </SocialLink>
              <SocialLink href="https://linkedin.com" label="LinkedIn">
                <LinkedInIcon />
              </SocialLink>
              <SocialLink href="https://twitter.com" label="Twitter / X">
                <XIcon />
              </SocialLink>
            </div>
          </div>

          {/* Link columns */}
          <LinkColumn
            heading={t('product.heading')}
            links={[
              { label: t('product.features'), to: '/#features' },
              { label: t('product.pricing'), to: '/pricing' },
              { label: t('product.roadmap'), to: '/#roadmap' },
              { label: t('product.changelog'), to: '/#changelog' },
            ]}
          />
          <LinkColumn
            heading={t('company.heading')}
            links={[
              { label: t('company.about'), to: '/#about' },
              { label: t('company.blog'), to: '/#blog' },
              { label: t('company.careers'), to: '/#careers' },
              { label: t('company.contact'), to: '/#contact' },
            ]}
          />
          <LinkColumn
            heading={t('resources.heading')}
            links={[
              { label: t('resources.docs'), to: '/#docs' },
              { label: t('resources.api'), to: '/#api' },
              { label: t('resources.status'), to: '/#status' },
              { label: t('resources.community'), to: '/#community' },
            ]}
          />
        </div>

        <div className="mt-16 flex flex-col gap-4 border-t border-border pt-8 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-muted-foreground">{t('copyright', { year: String(year) })}</p>
          <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
            <a href="/#terms" className="hover:text-foreground transition-colors">
              {t('legal.terms')}
            </a>
            <a href="/#privacy" className="hover:text-foreground transition-colors">
              {t('legal.privacy')}
            </a>
            <a href="/#security" className="hover:text-foreground transition-colors">
              {t('legal.security')}
            </a>
            <a href="/#dpa" className="hover:text-foreground transition-colors">
              {t('legal.dpa')}
            </a>
          </div>
        </div>
      </div>
    </footer>
  )
}

function LinkColumn({
  heading,
  links,
}: {
  heading: string
  links: Array<{ label: string; to: string }>
}) {
  return (
    <div>
      <h3 className="text-sm font-semibold tracking-wide uppercase text-foreground">{heading}</h3>
      <ul className="mt-4 space-y-3">
        {links.map((l) => (
          <li key={l.label}>
            {l.to.startsWith('/#') ? (
              <a
                href={l.to}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                {l.label}
              </a>
            ) : (
              <Link
                to={l.to}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                {l.label}
              </Link>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}

function SocialLink({
  href,
  label,
  children,
}: {
  href: string
  label: string
  children: React.ReactNode
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      className={cn(
        'inline-flex h-8 w-8 items-center justify-center rounded-full',
        'border border-border bg-background text-muted-foreground',
        'hover:text-foreground hover:border-foreground/40 transition-colors',
      )}
    >
      {children}
    </a>
  )
}

// ---------- Social icons (inline SVG) ----------
// lucide-react v1 dropped brand icons; rather than pull in another
// icon package for three glyphs, these are hand-inlined at 16x16.

function GitHubIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4" aria-hidden="true">
      <path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12" />
    </svg>
  )
}

function LinkedInIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4" aria-hidden="true">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.268 2.37 4.268 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.063 2.063 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  )
}

function XIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4" aria-hidden="true">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  )
}
